import React from "react";
import { css } from "@emotion/react";
import { useNavigate } from "react-router";

import { ReactTFC } from "../../../utils/fp/missing-ts-parts";

import { CSS_MQ } from "../../../styles/breakpoints";
import { CSS_COLORS } from "../../../styles/styles-css-variables";

import { InternalLinkProps, PlpSitemapLinker } from "../../../layouts/product-listing-page/sitemap/plp-sitemap";
import { ActivatedFacets, Facet, FilterValue, OpenedFilterTabByFilterKey as OpenedFilterTabFilterKey } from "./types";
import { ActiveFiltersBar } from "../../../layouts/product-listing-page/ProductListingPageFilter/ActiveFiltersBar";
import { getFullActiveFacets } from "../../../layouts/product-listing-page/ProductListingPageFilter/getFullActiveFacets";
import { GenericFacetedFilter } from "./GenericFacetedFilter";
import { OpenedFacetTab } from "./OpenedFacetTab";
import { firstFromNullable } from "./firstFromNullable";
import { LoadingState } from "../../../domain/data-layer/loading-state";
import type { TranslatedValue } from "@swo/storefront";
import { T } from "../../T";

type ProductListingPageFilterProps = {
  loadingState: LoadingState<{
    activatedFacets: ActivatedFacets;
    openedFilterTabByFilterKey: OpenedFilterTabFilterKey;
    facets: Array<Facet>;
  }>;

  isRefetching: boolean;
  sitemapLinker: PlpSitemapLinker;
  colorsCssMap: Record<string, string>;
  inputType: "checkbox" | "radio";
  label: TranslatedValue;
};

export const ProductListingPageFilter: ReactTFC<ProductListingPageFilterProps> = ({
  loadingState,
  colorsCssMap,
  isRefetching: refetching,
  sitemapLinker,
  inputType,
  label,
}) => {
  const navigate = useNavigate();

  if (loadingState.__tag !== "loaded") {
    return null;
  }

  const { openedFilterTabByFilterKey, activatedFacets, facets } = loadingState.data;

  const filterValues = facets.find(f => f.__key === openedFilterTabByFilterKey)?.values;
  const fullActiveFacets = getFullActiveFacets(activatedFacets, facets);

  const isChecked =
    (facetKey: string) =>
    (v: FilterValue): boolean => {
      return !!activatedFacets[facetKey] && firstFromNullable(activatedFacets[facetKey]) === v.value.toString();
    };

  const onFilterValueClicked =
    (openedFilterTabByFilterKey: string) =>
    (value: FilterValue): void | Promise<void> => {
      const current = firstFromNullable(activatedFacets[openedFilterTabByFilterKey]);
      const t = value.value?.toString();
      const nextValue = typeof current === "string" && current === t ? [] : [t];

      return navigate({
        ...sitemapLinker("facetedSearch", {
          activatedFacets: { ...activatedFacets, [openedFilterTabByFilterKey]: nextValue },
          openedFilterTabByFilterKey,
        }),
      });
    };

  const openedFacetTab =
    openedFilterTabByFilterKey && filterValues ? (
      <OpenedFacetTab
        activatedFacets={activatedFacets}
        filterValues={filterValues}
        colorsCssMap={colorsCssMap}
        openedFilterTabByFilterKey={openedFilterTabByFilterKey}
        inputType={inputType}
        isChecked={isChecked(openedFilterTabByFilterKey)}
        onFilterValueClicked={onFilterValueClicked(openedFilterTabByFilterKey)}
        allowMultipleSelection={false}
      />
    ) : null;

  const toggleFilterValue = (key: NonNullable<OpenedFilterTabFilterKey>, value: FilterValue["value"]): void => {
    const afk = activatedFacets[key];
    const nextValue = firstFromNullable(afk) === value.toString() ? [] : typeof afk === "undefined" ? [] : afk;

    const linkProps: InternalLinkProps = sitemapLinker("facetedSearch", {
      openedFilterTabByFilterKey,
      activatedFacets: {
        ...activatedFacets,
        [key]: nextValue,
      },
    });

    void navigate(linkProps);
  };

  const onClick = (facet: Facet) =>
    navigate({
      ...sitemapLinker("facetedSearch", {
        activatedFacets: activatedFacets,
        openedFilterTabByFilterKey: openedFilterTabByFilterKey === facet.__key ? undefined : facet.__key,
      }),
    });

  return (
    <>
      <GenericFacetedFilter
        isFetching={refetching}
        label={<T>{label}</T>}
        activatedFacets={activatedFacets}
        facets={facets}
        onClick={facet => {
          void onClick(facet);
        }}
        openedFilterTabByFilterKey={openedFilterTabByFilterKey}
      >
        {openedFacetTab}
      </GenericFacetedFilter>

      <ActiveFiltersBar fullActiveFacets={fullActiveFacets} filterRemovalRequested={toggleFilterValue} />
    </>
  );
};

export const iconComponentStyles = css`
  display: none;

  ${CSS_MQ.atLeastMd} {
    display: block;

    margin-right: 12px;
    flex-shrink: 0;
  }
`;

export const divStyles = css`
  display: flex;
  align-items: center;

  overflow-x: auto;
  overflow-y: hidden;

  ${CSS_MQ.atMostSm} {
    display: none;
    padding-top: 12px;

    padding-left: 12px;
    padding-right: 18px;

    .opened & {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }
  }

  &::-webkit-scrollbar {
    height: 5px;
  }

  &::-webkit-scrollbar-track {
    background: ${CSS_COLORS.Grey6};
  }

  &::-webkit-scrollbar-thumb {
    background: ${CSS_COLORS.Green};
    border-radius: 4px;
  }

  padding-top: 5px;
  margin-top: -5px;
`;
